// @flow
import { keyBy, noop, map, without, omit } from 'lodash';

import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  SET_CURRENT_GROUP
} from '../constants/actionTypes';
import decodeGroup from '../normalizers/decodeGroup';
import { NEW_GROUP_ID } from '../constants/friendConstants';
import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';
import {
  GROUP,
  GROUPS,
  UPDATE_FRIEND_GROUPS,
  DELETE_GROUP,
  CREATE_GROUP,
  UPDATE_GROUP,
  CREATE_FRIEND_REQUEST,
  ACCEPT_FRIEND_REQUEST,
  REMOVE_FRIEND,
  REMOVE_FRIEND_REQUEST,
  USER_SIGN_OUT,
  ADD_FRIEND_TO_GROUP,
  REMOVE_FRIEND_FROM_GROUP
} from '../constants/apiTypes';

const initialState: FriendGroupState = {
  hasInitialised: false,
  groups: {},
  errors: null,
  currentGroup: undefined,
  savingGroupFailed: false,
  groupCreateFailed: false,
  savingGroups: new Set(),
  deletingGroups: new Set(),
  deletingGroupsFailed: new Set(),
  isFetchingGroup: false,
  isFetchingGroups: true,
  isUpdating: false
};

function onUpdateFriendGroups(
  state: FriendGroupState,
  action: {
    friendId: number,
    addedGroups: Array<number>,
    removedGroups: Array<number>
  }
) {
  const addToGroups = (allGroups: *) => {
    const groups = action.addedGroups
      .map(id => ({ ...allGroups[id] }))
      .map(group => ({
        ...group,
        members: [...group.members, action.friendId]
      }));
    return {
      ...allGroups,
      ...keyBy(groups, 'id')
    };
  };

  const removeFromGroups = (allGroups: *) => {
    const groups = action.removedGroups
      .map(id => ({ ...allGroups[id] }))
      .map(group => ({
        ...group,
        members: without(group.members, action.friendId)
      }));

    return {
      ...allGroups,
      ...keyBy(groups, 'id')
    };
  };

  const newGroups = addToGroups(removeFromGroups(state.groups));
  const nextGroups = keyBy(newGroups, 'id');
  return {
    groups: nextGroups,
    isUpdating: false,
    savingGroups: new Set(without(...state.savingGroups, action.groupIds))
  };
}

function onAddFriendToGroup(state: FriendGroupState, { groupId, friendId }) {
  const oldGroup = state.groups[groupId];
  return {
    groups: {
      ...state.groups,
      [groupId]: {
        ...oldGroup,
        members: [...oldGroup.members, friendId]
      }
    },
    savingGroups: new Set(without(...state.savingGroups, groupId))
  };
}

function onRemoveFromGroup(state: FriendGroupState, { groupId, friendId }) {
  const oldGroup = state.groups[groupId];
  return {
    groups: {
      ...state.groups,
      [groupId]: {
        ...oldGroup,
        members: without(oldGroup.members, friendId)
      }
    },
    savingGroups: new Set(without(...state.savingGroups, groupId))
  };
}

function onFetchGroups(state: FriendGroupState, action: { data: Object }) {
  return {
    hasInitialised: true,
    isFetchingGroups: false,
    groups: {
      ...keyBy(action.data.map(decodeGroup), 'id')
    }
  };
}

function onGetGroup(state: FriendGroupState, action: { data: Object }) {
  return {
    isFetchingGroup: false,
    groups: {
      ...state.groups,
      [action.data.id]: decodeGroup(action.data)
    }
  };
}

function onUpdateGroup(state: FriendGroupState, action: { data: Object }) {
  const updateGroup = decodeGroup(action.data);
  return {
    isUpdating: false,
    savingGroupFailed: false,
    savingGroups: new Set(without(...state.savingGroups, updateGroup.id)),
    groups: {
      ...state.groups,
      [action.data.id]: updateGroup
    },
    currentGroup: updateGroup.id
  };
}

function onCreateGroup(state: FriendGroupState, action: { data: Object }) {
  const newGroup = decodeGroup(action.data);
  return {
    isUpdating: false,
    savingGroupFailed: false,
    savingGroups: new Set(without(...state.savingGroups, newGroup.id)),
    groups: {
      ...omit(state.groups, NEW_GROUP_ID),
      [action.data.id]: newGroup
    },
    currentGroup: newGroup.id
  };
}

function onDeleteGroup(state: FriendGroupState, action: { id: number }) {
  return {
    groups: omit(state.groups, action.id),
    isUpdating: false,
    deletingGroups: new Set(without(...state.deletingGroups, action.id)),
    savingGroups: new Set(without(...state.savingGroups, action.id))
    // currentGroup: FRIEND_GROUP_ID,
  };
}

function onAddMembers(state, action: { members: Array<Number>, groupId: number }) {
  const { groupId } = action;
  if (!groupId && !state.groups[groupId]) {
    return {};
  }

  const nextGroup = {
    ...state.groups[groupId],
    members: [...state.groups[groupId].members, ...action.members]
  };

  return {
    // if they specified a group id, add the request to the group too
    groups: {
      ...state.groups,
      [groupId]: nextGroup
    },
    currentGroup: nextGroup.id
  };
}

function onUserRemoved(state, action: { id: number }) {
  const nextGroups = keyBy(
    map(state.groups, group => ({
      ...group,
      members: without(group.members, action.id)
    })),
    'id'
  );
  return {
    groups: nextGroups
  };
}

const apiRequestReducer = switchFunc(
  {
    [GROUPS]: () => ({
      errors: null,
      isFetchingGroups: true
    }),
    [GROUP]: () => ({
      errors: null,
      isFetchingGroup: true
    }),
    [CREATE_GROUP]: (state: FriendGroupState, action) => ({
      errors: null,
      savingGroups: new Set([...state.savingGroups, action.groupId]),
      savingGroupFailed: false,
      isUpdating: true
    }),
    [UPDATE_GROUP]: (state: FriendGroupState, action) => ({
      errors: null,
      savingGroups: new Set([...state.savingGroups, action.groupId]),
      savingGroupFailed: false,
      isUpdating: true
    }),
    [DELETE_GROUP]: (state: FriendGroupState, { id }) => ({
      deletingGroups: new Set([...state.deletingGroups, id]),
      deletingGroupsFailed: new Set(without(state.deletingGroupsFailed, id)),
      errors: null,
      isUpdating: true
    }),
    [UPDATE_FRIEND_GROUPS]: (state: FriendGroupState, action) => ({
      errors: null,
      savingGroups: new Set([...state.savingGroups, action.groupId])
    }),
    [ADD_FRIEND_TO_GROUP]: (state: FriendGrouPState, action) => ({
      errors: null,
      savingGroups: new Set([...state.savingGroups, action.groupId])
    })
  },
  () => noop
);

const apiSuccessReducer = switchFunc(
  {
    [GROUP]: onGetGroup,
    [GROUPS]: onFetchGroups,
    [UPDATE_GROUP]: onUpdateGroup,
    [CREATE_GROUP]: onCreateGroup,
    [DELETE_GROUP]: onDeleteGroup,
    [UPDATE_FRIEND_GROUPS]: onUpdateFriendGroups,
    [REMOVE_FRIEND]: (state, action) =>
      onUserRemoved(state, { id: action.friendId, groupId: action.groupId }),
    [REMOVE_FRIEND_REQUEST]: (state, action) =>
      onUserRemoved(state, { id: action.id, groupId: action.groupId }),
    [CREATE_FRIEND_REQUEST]: (state, action) =>
      onAddMembers(state, {
        members: map(action.friendRequests, 'recipient.id'),
        groupId: action.groupId
      }),
    [ACCEPT_FRIEND_REQUEST]: (state, action) =>
      onAddMembers(state, { members: [action.friend.id], groupId: action.groupId }),
    [USER_SIGN_OUT]: () => initialState,
    [ADD_FRIEND_TO_GROUP]: onAddFriendToGroup,
    [REMOVE_FRIEND_FROM_GROUP]: onRemoveFromGroup
  },
  () => noop
);

const apiFailureReducer = switchFunc(
  {
    [GROUP]: (state, action) => ({
      errors: action.errors,
      isFetchinGroup: false
    }),
    [GROUPS]: (state, action) => ({
      errors: action.errors,
      isFetchingGroups: false
    }),
    [CREATE_GROUP]: (state: FriendGroupState, action) => ({
      errors: action.errors,
      savingGroups: new Set(without(...state.savingGroups, action.groupId)),
      savingGroupFailed: true
    }),
    [UPDATE_GROUP]: (state: FriendGroupState, action) => ({
      errors: action.errors,
      savingGroups: new Set(without(...state.savingGroups, action.groupId)),
      savingGroupFailed: true
    }),
    [DELETE_GROUP]: (state, action) => ({
      errors: action.errors,
      deletingGroups: new Set(without(state.deletingGroups, action.id)),
      deleteGroupFailed: new Set([...state.deletingGroupsFailed, action.id])
    }),
    [UPDATE_FRIEND_GROUPS]: (state: FriendGroupState, action) => ({
      errors: action.errors,
      savingGroups: new Set(without(...state.savingGroups, action.groupId))
    }),
    [ADD_FRIEND_TO_GROUP]: (state: FriendGroupState, action) => ({
      errors: action.errors,
      savingGroups: new Set(without(...state.savingGroups, action.groupId))
    }),
    [REMOVE_FRIEND_FROM_GROUP]: (state: FriendGroupState, action) => ({
      errors: action.errors,
      savingGroups: new Set(without(...state.savingGroups, action.groupId))
    })
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => ({
    ...apiRequestReducer(action.api)(state, action)
  }),
  [API_CALL_SUCCESS]: (state, action) => ({
    ...apiSuccessReducer(action.api)(state, action)
  }),
  [API_CALL_FAILURE]: (state, action) => ({
    ...apiFailureReducer(action.api)(state, action)
  }),
  [SET_CURRENT_GROUP]: (state, action) => ({
    currentGroup: action.groupId
  })
});
